/**
 * Structured Data (JSON-LD) Generators
 * UTF-8 safe for Turkish content
 */

import { getFullUrl } from './seo';

const BASE_URL = 'https://studioasimmete.com';

export interface Project {
  id: number;
  title: string;
  category: string;
  location: string;
  year: string;
  image: string;
  description: string;
  images?: string[];
}

/**
 * Organization Schema
 */
export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Studio Asım Mete',
    alternateName: 'Studio Asim Mete',
    url: BASE_URL,
    logo: `${BASE_URL}/asimlogo.png`,
    description: 'İstanbul\'da minimalist ve lüks iç mimari tasarım hizmetleri sunan öncü stüdyo. Mekânsal formda sessiz otorite yaratıyoruz.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4. Levent Otosanayi Sitesi, Barbaros Cd No:4',
      addressLocality: 'Sarıyer',
      addressRegion: 'İstanbul',
      postalCode: '34394',
      addressCountry: 'TR'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+90-537-961-99-20',
      contactType: 'customer service',
      areaServed: 'TR',
      availableLanguage: ['Turkish', 'English']
    },
    sameAs: [
      // Social media links can be added here
    ],
    areaServed: {
      '@type': 'City',
      name: 'İstanbul'
    }
  };
}

/**
 * WebSite Schema
 */
export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Studio Asım Mete',
    url: BASE_URL,
    description: 'İstanbul\'da minimalist ve lüks iç mimari tasarım hizmetleri. Ofis, konut ve ticari mekan tasarımında uzman.',
    publisher: {
      '@type': 'Organization',
      name: 'Studio Asım Mete'
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/projeler?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
}

/**
 * BreadcrumbList Schema
 */
export function getBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
    }))
  };
}

/**
 * Project Schema (CreativeWork/Project)
 */
export function getProjectSchema(project: Project) {
  const fullImageUrl = project.image.startsWith('http') 
    ? project.image 
    : getFullUrl(project.image);
  
  const imageList = project.images 
    ? project.images.map(img => img.startsWith('http') ? img : getFullUrl(img))
    : [];

  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    '@id': `${BASE_URL}/proje/${project.id}`,
    name: project.title,
    description: project.description,
    image: [fullImageUrl, ...imageList],
    creator: {
      '@type': 'Organization',
      name: 'Studio Asım Mete',
      url: BASE_URL
    },
    dateCreated: project.year,
    locationCreated: {
      '@type': 'Place',
      name: project.location
    },
    keywords: `iç mimari, ${project.category}, ${project.location}, ${project.year}`,
    inLanguage: 'tr-TR',
    genre: project.category === 'konut' ? 'Residential Design' : 
           project.category === 'ofis' ? 'Office Design' : 
           'Commercial Design'
  };
}

/**
 * LocalBusiness Schema (for contact page)
 */
export function getLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${BASE_URL}/iletisim`,
    name: 'Studio Asım Mete',
    image: `${BASE_URL}/asimlogo.png`,
    description: 'İstanbul\'da minimalist ve lüks iç mimari tasarım hizmetleri sunan öncü stüdyo.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4. Levent Otosanayi Sitesi, Barbaros Cd No:4',
      addressLocality: 'Sarıyer',
      addressRegion: 'İstanbul',
      postalCode: '34394',
      addressCountry: 'TR'
    },
    telephone: '+90-537-961-99-20',
    priceRange: '$$$',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '10:00',
        closes: '15:00'
      }
    ],
    areaServed: {
      '@type': 'City',
      name: 'İstanbul'
    }
  };
}
